import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { hasRole, useApi, useApp } from "../context/AppContext";
import { PLATFORM_LABEL, STATUS_STYLE, codeVersionOf, driftOf, loadFleet, modelsOf, platformOf } from "../lib/fleet";

// One cabinet type (docs/tasks/35, level T): its hardware, its cabinets, and what
// is trained on it. Rows taught in a training visit transfer only between
// cabinets with the same lens, light and mount, so the type is what is trained.

const MOUNTS = ["top", "bottom", "side"];
const EMPTY = { name: "", model: "chest", light: "", cameras: [{ id: 0, mount: "top", resolution: "" }], shelves: [{ id: "1", name: "shelf 1", cameras: [0] }] };

// What is missing or contradictory in a type's description.
function problemsOf(t) {
  const out = [];
  if (!t.light) out.push("No light described: the cabinet's light is part of what the rows learn.");
  if (!(t.cameras || []).length) out.push("No camera.");
  if ((t.cameras || []).some((c) => !c.mount)) out.push("A camera has no mount: training then requires every camera for every product.");
  if ((t.cameras || []).length && !(t.cameras || []).some((c) => c.mount === "top")) out.push("No camera is mounted 'top': the review camera that decides purchases.");
  if (!(t.shelves || []).length) out.push("No shelves: training coverage is counted per shelf.");
  return out;
}

function HardwareForm({ initial, onSave, saving, readOnly }) {
  const [t, setT] = useState(initial);
  useEffect(() => setT(initial), [initial]);
  const set = (k, v) => setT((x) => ({ ...x, [k]: v }));
  const setCam = (i, k, v) => set("cameras", t.cameras.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const setShelf = (i, k, v) => set("shelves", t.shelves.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const camIds = t.cameras.map((c) => c.id);
  const problems = problemsOf(t);
  return (
    <form className="card space-y-4" onSubmit={(e) => { e.preventDefault(); onSave(t); }}>
      <h2 className="text-sm font-semibold">Hardware</h2>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-xs">Name<input className="input" required maxLength={80} disabled={readOnly} value={t.name} onChange={(e) => set("name", e.target.value)} /></label>
        <label className="text-xs">Model
          <select className="input" disabled={readOnly} value={t.model} onChange={(e) => set("model", e.target.value)}>
            <option value="chest">chest (top-opening)</option>
            <option value="cabinet">cabinet (upright, shelves)</option>
          </select>
        </label>
        <label className="text-xs">Light<input className="input" disabled={readOnly} placeholder="e.g. white LED strip under the lid" value={t.light || ""} onChange={(e) => set("light", e.target.value)} /></label>
      </div>

      <div>
        <div className="label">Cameras</div>
        <table className="table">
          <thead><tr><th>Id</th><th>Mount</th><th>Sensor / resolution</th><th /></tr></thead>
          <tbody>
            {t.cameras.map((c, i) => (
              <tr key={i}>
                <td><input className="input w-20" type="number" min={0} disabled={readOnly} value={c.id} onChange={(e) => setCam(i, "id", Number(e.target.value))} /></td>
                <td>
                  <input className="input" list="mounts" disabled={readOnly} value={c.mount || ""} onChange={(e) => setCam(i, "mount", e.target.value)} />
                </td>
                <td><input className="input" disabled={readOnly} placeholder="IMX219 1920×1080" value={c.resolution || ""} onChange={(e) => setCam(i, "resolution", e.target.value)} /></td>
                <td>{!readOnly && <button type="button" className="text-xs text-rose-700" onClick={() => set("cameras", t.cameras.filter((_, j) => j !== i))}>remove</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <datalist id="mounts">{MOUNTS.map((m) => <option key={m} value={m} />)}</datalist>
        {!readOnly && (
          <button type="button" className="btn btn-secondary mt-2 text-xs"
            onClick={() => set("cameras", [...t.cameras, { id: Math.max(-1, ...camIds) + 1, mount: "", resolution: "" }])}>Add camera</button>
        )}
      </div>

      <div>
        <div className="label">Shelves and the cameras that see them</div>
        <table className="table">
          <thead><tr><th>Id</th><th>Name</th><th>Seen by</th><th /></tr></thead>
          <tbody>
            {t.shelves.map((s, i) => (
              <tr key={i}>
                <td><input className="input w-20" disabled={readOnly} value={s.id} onChange={(e) => setShelf(i, "id", e.target.value)} /></td>
                <td><input className="input" disabled={readOnly} value={s.name} onChange={(e) => setShelf(i, "name", e.target.value)} /></td>
                <td className="text-xs">
                  {camIds.map((id) => (
                    <label key={id} className="mr-3 inline-flex items-center gap-1">
                      <input type="checkbox" disabled={readOnly} checked={(s.cameras || []).includes(id)}
                        onChange={(e) => setShelf(i, "cameras", e.target.checked ? [...(s.cameras || []), id] : (s.cameras || []).filter((x) => x !== id))} />
                      {id}
                    </label>
                  ))}
                </td>
                <td>{!readOnly && <button type="button" className="text-xs text-rose-700" onClick={() => set("shelves", t.shelves.filter((_, j) => j !== i))}>remove</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!readOnly && (
          <button type="button" className="btn btn-secondary mt-2 text-xs"
            onClick={() => { const n = t.shelves.length + 1; set("shelves", [...t.shelves, { id: String(n), name: `shelf ${n}`, cameras: camIds }]); }}>Add shelf</button>
        )}
      </div>

      {problems.length > 0 && (
        <ul className="list-disc rounded border border-amber-300 bg-amber-50 py-2 pl-8 pr-3 text-xs text-amber-800">
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      {!readOnly && <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save the type"}</button>}
      {!readOnly && initial.id && (
        <p className="text-xs text-slate-500">Removing a camera or a shelf does not delete the rows trained on it; they stop counting towards coverage.</p>
      )}
    </form>
  );
}

const ENGINES = ["trt_fp16", "trt_fp32", "hailo", "onnx_cuda", "onnx_cpu"];
const WINDOW_MODES = ["grid", "object", "detector"];
// What the device reports (trt_plan, onnx_trt) as the kinds a type expects.
const AS_KIND = { trt_plan: "trt_fp16", onnx_trt: "onnx_cuda" };

// The platform and what a cabinet of this type should run (docs/tasks/35, phase 3).
// Saved with the hardware (PUT replaces the type); keys this form does not show
// - the detector build's trained_on and acceptance, other boards' engines - are kept.
function ExpectedForm({ type, cabinets, onSave, saving, readOnly }) {
  const [platform, setPlatform] = useState(type.platform || "");
  const [weighed, setWeighed] = useState(!!type.weighed_trays);
  const [exp, setExp] = useState(type.expected || {});
  const [configText, setConfigText] = useState(JSON.stringify(type.expected?.config || {}, null, 1));
  const [err, setErr] = useState(null);
  useEffect(() => {
    setPlatform(type.platform || ""); setWeighed(!!type.weighed_trays); setExp(type.expected || {});
    setConfigText(JSON.stringify(type.expected?.config || {}, null, 1));
  }, [type]);
  const det = exp.detector || {};
  const eng = (det.engines || {})[platform] || {};
  const setDet = (k, v) => setExp((e) => ({ ...e, detector: { ...(e.detector || {}), [k]: v || undefined } }));
  const setEng = (k, v) => setExp((e) => {
    const d = e.detector || {};
    return { ...e, detector: { ...d, engines: { ...(d.engines || {}), [platform]: { ...((d.engines || {})[platform] || {}), [k]: v || undefined } } } };
  });
  const fromCabinet = (id) => {
    const cab = cabinets.find((c) => c.id === id);
    const m = modelsOf(cab?.health);
    setExp((e) => {
      const next = { ...e };
      if (m.encoder?.version) next.encoder_version = m.encoder.version;
      if (cab?.health?.report?.review_window_mode) next.review_window_mode = cab.health.report.review_window_mode;
      if (m.detector) {
        const board = platform || platformOf(cab);
        const d = { ...(e.detector || {}), name: m.detector.name };
        if (board) {
          const kind = AS_KIND[m.detector.engine] || m.detector.engine;
          d.engines = { ...(d.engines || {}), [board]: { ...((d.engines || {})[board] || {}), kind,
            ...(kind === "hailo" ? { hef_sha256: m.detector.file_sha256 } : { sha256: m.detector.file_sha256 }) } };
        }
        next.detector = d;
      }
      return next;
    });
    if (!platform && platformOf(cab)) setPlatform(platformOf(cab));
  };
  const submit = (e) => {
    e.preventDefault(); setErr(null);
    let config;
    try { config = JSON.parse(configText || "{}"); } catch { setErr("Config is not valid JSON."); return; }
    const clean = JSON.parse(JSON.stringify({ ...exp, config }));
    for (const k of Object.keys(clean)) if (clean[k] === "" || (typeof clean[k] === "object" && !Object.keys(clean[k]).length)) delete clean[k];
    onSave({ platform: platform || null, weighed_trays: weighed, expected: clean });
  };
  const reporting = cabinets.filter((c) => c.health?.report?.models);
  return (
    <form className="card space-y-4" onSubmit={submit}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Platform and expected models</h2>
          <p className="text-xs text-slate-500">What every cabinet of this type should run. Each cabinet's report is compared with it; a difference shows on the fleet page.</p>
        </div>
        {!readOnly && reporting.length > 0 && (
          <select className="input w-auto py-1 text-xs" value="" onChange={(e) => e.target.value && fromCabinet(e.target.value)}>
            <option value="">Take what a cabinet runs…</option>
            {reporting.map((c) => <option key={c.id} value={c.id}>{c.device?.name || c.id}</option>)}
          </select>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-xs">Platform
          <select className="input" disabled={readOnly} value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="">not set</option>
            {Object.entries(PLATFORM_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="flex items-end gap-2 text-xs">
          <input type="checkbox" disabled={readOnly} checked={weighed} onChange={(e) => setWeighed(e.target.checked)} />
          weighed trays (its cabinets should report a weight board)
        </label>
        <label className="text-xs">Review window mode
          <select className="input" disabled={readOnly} value={exp.review_window_mode || ""} onChange={(e) => setExp({ ...exp, review_window_mode: e.target.value || undefined })}>
            <option value="">not set</option>
            {WINDOW_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="text-xs">Encoder version<input className="input" disabled={readOnly} placeholder="1.0.0" value={exp.encoder_version || ""} onChange={(e) => setExp({ ...exp, encoder_version: e.target.value || undefined })} /></label>
        <label className="text-xs">Detector<input className="input" disabled={readOnly} placeholder="held_tiny" value={det.name || ""} onChange={(e) => setDet("name", e.target.value)} /></label>
        <label className="text-xs">Detector version<input className="input" disabled={readOnly} placeholder="2026.10.22-v3e" value={det.version || ""} onChange={(e) => setDet("version", e.target.value)} /></label>
        <label className="text-xs">Detector engine on {PLATFORM_LABEL[platform] || "this platform"}
          <select className="input" disabled={readOnly || !platform} value={eng.kind || ""} onChange={(e) => setEng("kind", e.target.value)}>
            <option value="">{platform ? "not set" : "set the platform first"}</option>
            {ENGINES.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label className="text-xs">Engine file sha256 (prefix)
          <input className="input font-mono" disabled={readOnly || !platform} value={(eng.kind === "hailo" ? eng.hef_sha256 : eng.sha256) || ""}
            onChange={(e) => setEng(eng.kind === "hailo" ? "hef_sha256" : "sha256", e.target.value.trim().toLowerCase())} />
        </label>
        <label className="text-xs">Sensor board firmware<input className="input" disabled={readOnly} placeholder="0.3.0" value={exp.pico_fw || ""} onChange={(e) => setExp({ ...exp, pico_fw: e.target.value || undefined })} /></label>
      </div>
      <label className="block text-xs">Configuration defaults (wire keys, JSON)
        <textarea className="input h-24 font-mono text-xs" disabled={readOnly} value={configText} onChange={(e) => setConfigText(e.target.value)} />
      </label>
      {(det.trained_on || det.acceptance) && (
        <p className="text-xs text-slate-600">
          Detector build: {det.trained_on ? `trained on ${JSON.stringify(det.trained_on)}` : ""} {det.acceptance ? `· acceptance ${JSON.stringify(det.acceptance)}` : ""}
        </p>
      )}
      {err && <p className="text-xs text-rose-700">{err}</p>}
      {!readOnly && <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Save platform and models"}</button>}
    </form>
  );
}

function ModelCells({ cab }) {
  const m = modelsOf(cab.health);
  if (!cab.health?.report?.models) return <td colSpan={3} className="text-xs text-slate-400">not reported (runtime before phase 3)</td>;
  return (
    <>
      <td className="text-xs">{m.encoder ? `${m.encoder.engine || "?"}${m.encoder.self_check != null ? ` · ${m.encoder.self_check}` : ""}` : "—"}</td>
      <td className="text-xs">{m.detector ? `${m.detector.name} · ${m.detector.engine || "?"}${m.detector.loaded === false ? " · not loaded" : ""}` : "none"}</td>
      <td className="text-xs">{cab.health.report.review_window_mode || "—"}</td>
    </>
  );
}

function TrainingSummary({ status, sets, names }) {
  if (!status) return null;
  const products = status.products || [];
  const order = { untrained: 0, partial: 1, trained: 2 };
  const sorted = [...products].sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3));
  const active = sets.filter((s) => s.active);
  return (
    <div className="card overflow-x-auto">
      <h2 className="mb-1 text-sm font-semibold">Training on this type</h2>
      <p className="mb-2 text-xs text-slate-500">
        {active.length} active enrolment sets of {sets.length} · {status.negative_rows ?? 0} negative rows · a camera counts at {status.min_rows} rows ·
        {status.trusted ? " every product trained" : " not every product trained"}
      </p>
      {sorted.length === 0 ? <p className="text-sm text-slate-500">Nothing trained on this type yet.</p> : (
        <table className="table">
          <thead><tr><th>Product</th><th>Status</th><th>Rows by camera</th><th>Required cameras</th><th>Separability</th><th>Nearest look-alike</th><th>Last trained</th></tr></thead>
          <tbody>
            {sorted.map((p) => (
              <tr key={p.product_uuid}>
                <td>{names[p.product_uuid] || p.product_uuid.slice(0, 8)}</td>
                <td className={p.status === "trained" ? "text-emerald-700" : p.status === "partial" ? "text-amber-700" : "text-rose-700"}>{p.status}</td>
                <td className="font-mono text-xs">{Object.entries(p.rows_by_camera || {}).map(([c, n]) => `${c}: ${n}`).join(" · ") || "—"}</td>
                <td className="text-xs">{(p.required_cameras || []).join(", ")}</td>
                <td className="text-xs">{p.separability != null ? p.separability.toFixed(2) : "—"}{p.confident === false ? " (weak)" : ""}</td>
                <td className="text-xs">{p.nearest_product ? (names[p.nearest_product] || String(p.nearest_product).slice(0, 8)) : "—"}</td>
                <td className="text-xs">{p.last_trained_at ? new Date(p.last_trained_at * 1000).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function CabinetTypePage() {
  const { typeId } = useParams();
  const isNew = typeId === "new";
  const api = useApi();
  const { user } = useApp();
  const navigate = useNavigate();
  const [fleet, setFleet] = useState(null);
  const [status, setStatus] = useState(null);
  const [sets, setSets] = useState([]);
  const [names, setNames] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const mayEdit = hasRole(user, "tenant_admin", "device_operator", "ml_operator");

  const reload = () => loadFleet(api).then(setFleet).catch((e) => setErr(String(e.message || e)));
  useEffect(() => {
    void reload();
    api.listProducts().then((list) => {
      const out = {};
      for (const p of list || []) out[p.id] = p.product_name || p.ean_code || p.id.slice(0, 8);
      setNames(out);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  useEffect(() => {
    if (isNew) return;
    api.getCabinetTypeTrainingStatus(typeId).then(setStatus).catch(() => setStatus(null));
    api.listEnrolmentSets(typeId).then(setSets).catch(() => setSets([]));
  }, [api, typeId, isNew]);

  const type = useMemo(() => (isNew ? EMPTY : fleet?.types.find((t) => t.id === typeId)), [fleet, typeId, isNew]);
  if (err) return <p className="text-sm text-rose-700">{err}</p>;
  if (!fleet) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!type) return <p className="text-sm text-rose-700">No cabinet type {typeId}.</p>;
  const cabinets = fleet.cabinets.filter((c) => c.type?.id === typeId);

  const save = async (t) => {
    setSaving(true); setMsg(null); setErr(null);
    const body = { name: t.name, model: t.model, light: t.light || null, cameras: t.cameras, shelves: t.shelves,
                   ...(t.platform !== undefined ? { platform: t.platform, weighed_trays: t.weighed_trays, expected: t.expected } : {}) };
    try {
      if (isNew) {
        const created = await api.createCabinetType(body);
        navigate(`/types/${created.id}`, { replace: true });
      } else {
        await api.updateCabinetType(typeId, body);
        setMsg("Saved.");
      }
      await reload();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs text-slate-500"><Link className="hover:underline" to="/">Fleet</Link> › {isNew ? "new type" : type.name}</div>
        <h1 className="text-2xl font-semibold">{isNew ? "New cabinet type" : type.name}</h1>
        {!isNew && <p className="font-mono text-xs text-slate-500">{type.id}</p>}
      </div>
      {msg && <p className="text-sm text-emerald-700">{msg}</p>}
      <HardwareForm initial={type} onSave={save} saving={saving} readOnly={!mayEdit} />
      {!isNew && (
        <ExpectedForm type={type} cabinets={cabinets} saving={saving} readOnly={!mayEdit}
          onSave={(extra) => save({ ...type, ...extra })} />
      )}
      {!isNew && (
        <div className="card overflow-x-auto">
          <h2 className="mb-2 text-sm font-semibold">Cabinets of this type</h2>
          {cabinets.length === 0 ? <p className="text-sm text-slate-500">No cabinet is of this type yet. Assign one from its page.</p> : (
            <table className="table">
              <thead><tr><th>Cabinet</th><th>Status</th><th>Code</th><th>Gallery</th><th>Encoder</th><th>Detector</th><th>Windows</th><th>Differs from the type</th></tr></thead>
              <tbody>
                {cabinets.map((c) => {
                  const drift = driftOf(c, type);
                  return (
                    <tr key={c.id}>
                      <td><Link className="text-sky-700 hover:underline" to={`/cabinets/${c.id}`}>{c.device?.name || c.id}</Link></td>
                      <td><span className={`rounded px-2 py-0.5 text-xs ${STATUS_STYLE[c.health?.status] || STATUS_STYLE.unknown}`}>{c.health?.status || "unknown"}</span></td>
                      <td className="font-mono text-xs">{codeVersionOf(c) || "—"}</td>
                      <td className="font-mono text-xs">{c.health?.model?.reported || "—"}</td>
                      <ModelCells cab={c} />
                      <td className={`text-xs ${drift.length ? "text-amber-700" : "text-emerald-700"}`}>
                        {drift.length ? drift.map((d) => `${d.what}: ${d.actual} (expects ${d.expected})`).join("; ") : "matches"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
      {!isNew && <TrainingSummary status={status} sets={sets} names={names} />}
    </div>
  );
}
